import { z } from 'zod';

// ------------------------------------------------------------------ scopes
/** What an API key can reach, by area of the product. `<area>:write` includes reading. */
export const API_SCOPE_AREAS = ['workspaces', 'tasks', 'editorial', 'calendar', 'crm', 'finance', 'hr', 'tickets', 'chat', 'pages', 'social', 'general'] as const;
export type ApiScopeArea = (typeof API_SCOPE_AREAS)[number];
export const API_SCOPE_LABELS: Record<ApiScopeArea, string> = {
  workspaces: 'Spazi, membri e inviti',
  tasks: 'Task',
  editorial: 'Calendario editoriale',
  calendar: 'Calendario ed eventi',
  crm: 'CRM',
  finance: 'Contabilità',
  hr: 'Persone (ferie e permessi)',
  tickets: 'Ticket',
  chat: 'Chat (solo canali degli spazi)',
  pages: 'Documenti',
  social: 'Social: account, statistiche, campagne',
  general: 'Home, notifiche e ricerca',
};
export const API_SCOPES = API_SCOPE_AREAS.flatMap((a) => [`${a}:read`, `${a}:write`] as const);
export type ApiScope = `${ApiScopeArea}:${'read' | 'write'}`;

// ------------------------------------------------------------------ webhook events
/** Every event a webhook can receive. `data` is the same object the API returns for that action. */
export const WEBHOOK_EVENTS = {
  'workspace.created': 'Spazio creato',
  'workspace.updated': 'Spazio modificato',
  'workspace.deleted': 'Spazio eliminato',
  'workspace.member_added': 'Persona aggiunta a uno spazio',
  'workspace.member_updated': 'Ruolo nello spazio modificato',
  'workspace.member_removed': 'Persona tolta da uno spazio',
  'member.invited': 'Persona invitata nell’azienda',
  'member.updated': 'Ruolo nell’azienda modificato',
  'member.removed': 'Persona rimossa dall’azienda',
  'task.created': 'Task creato',
  'task.updated': 'Task modificato',
  'task.completed': 'Task completato',
  'task.reopened': 'Task riaperto',
  'task.deleted': 'Task eliminato',
  'task.comment_created': 'Commento a un task',
  'content.created': 'Contenuto editoriale creato',
  'content.updated': 'Contenuto editoriale modificato',
  'content.submitted': 'Contenuto inviato in revisione',
  'content.approved': 'Contenuto approvato',
  'content.changes_requested': 'Modifiche richieste su un contenuto',
  'content.deleted': 'Contenuto editoriale eliminato',
  'content.comment_created': 'Commento a un contenuto',
  'event.created': 'Evento creato',
  'event.updated': 'Evento modificato',
  'event.deleted': 'Evento eliminato',
  'deal.created': 'Trattativa creata',
  'deal.updated': 'Trattativa modificata',
  'deal.moved': 'Trattativa spostata di fase',
  'deal.deleted': 'Trattativa eliminata',
  'company.created': 'Azienda CRM creata',
  'company.updated': 'Azienda CRM modificata',
  'company.deleted': 'Azienda CRM eliminata',
  'contact.created': 'Contatto creato',
  'contact.updated': 'Contatto modificato',
  'contact.deleted': 'Contatto eliminato',
  'activity.created': 'Attività CRM registrata',
  'email.sent': 'Email inviata dal CRM',
  'transaction.created': 'Movimento contabile creato',
  'transaction.updated': 'Movimento contabile modificato',
  'transaction.deleted': 'Movimento contabile eliminato',
  'leave_request.created': 'Richiesta di ferie o permesso',
  'leave_request.reviewed': 'Richiesta di ferie approvata o rifiutata',
  'leave_request.deleted': 'Richiesta di ferie annullata',
  'ticket.created': 'Ticket aperto',
  'ticket.updated': 'Ticket modificato',
  'ticket.deleted': 'Ticket eliminato',
  'ticket.comment_created': 'Commento a un ticket',
  'channel.created': 'Canale chat creato',
  'message.created': 'Messaggio in un canale',
  'message.updated': 'Messaggio modificato',
  'page.created': 'Documento creato',
  'page.updated': 'Documento modificato',
  'page.deleted': 'Documento eliminato',
} as const;
export type WebhookEventType = keyof typeof WEBHOOK_EVENTS;
export const WEBHOOK_EVENT_TYPES = Object.keys(WEBHOOK_EVENTS) as WebhookEventType[];

// ------------------------------------------------------------------ inputs
export const CreateApiKeyInput = z.object({
  name: z.string().trim().min(1, 'Dai un nome alla chiave.').max(80),
  scopes: z.array(z.enum(API_SCOPES as unknown as [string, ...string[]])).min(1, 'Scegli almeno un’area.').max(40),
  expiresInDays: z.number().int().min(1).max(3650).nullable().optional(),
});
export type CreateApiKeyInput = z.infer<typeof CreateApiKeyInput>;

const webhookUrl = z.string().trim().url('Inserisci un indirizzo valido.').max(2000);
const webhookEvents = z.array(z.union([z.literal('*'), z.enum(WEBHOOK_EVENT_TYPES as [WebhookEventType, ...WebhookEventType[]])])).min(1, 'Scegli almeno un evento.').max(100);

export const CreateWebhookInput = z.object({
  url: webhookUrl,
  description: z.string().trim().max(200).nullable().optional(),
  events: webhookEvents,
});
export type CreateWebhookInput = z.infer<typeof CreateWebhookInput>;

export const UpdateWebhookInput = z.object({
  url: webhookUrl.optional(),
  description: z.string().trim().max(200).nullable().optional(),
  events: webhookEvents.optional(),
  active: z.boolean().optional(),
});
export type UpdateWebhookInput = z.infer<typeof UpdateWebhookInput>;

// ------------------------------------------------------------------ outputs
export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  createdBy: { id: string; displayName: string };
  createdAt: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
}
/** Returned once, at creation: the full key is never shown again. */
export interface CreatedApiKey extends ApiKey {
  key: string;
}

export interface Webhook {
  id: string;
  url: string;
  description: string | null;
  events: string[];
  active: boolean;
  disabledReason: string | null;
  createdAt: string;
  lastDelivery: { status: 'pending' | 'delivered' | 'failed'; statusCode: number | null; at: string } | null;
}
/** The signing secret is returned at creation and when rotated. */
export interface WebhookWithSecret extends Webhook {
  secret: string;
}

export interface WebhookDelivery {
  id: string;
  eventId: string;
  eventType: string;
  status: 'pending' | 'delivered' | 'failed';
  attempts: number;
  lastStatusCode: number | null;
  lastError: string | null;
  createdAt: string;
  deliveredAt: string | null;
  nextAttemptAt: string | null;
}

/** GET /api/v1/key: what the calling key is. */
export interface ApiKeyInfo {
  id: string;
  name: string;
  scopes: string[];
  organization: { id: string; slug: string; name: string };
  actsAs: { id: string; displayName: string; email: string };
  expiresAt: string | null;
}
